import { useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '@/contexts/AuthContext';
import { useOrganizationId } from '@/contexts/OrganizationContext';
import { SnackbarContextType } from '@/contexts/SnackbarContext.types';
import { createApiClient } from '@/shared/api/apiClient';
import type { components } from '@/shared/api/openapi';
import { handleSnackbarError } from '@/shared/utils/errorHandler';

export interface UserBudgetGroup {
  userId: string;
  budgetGroupId: number;
  organizationId: number;
  role: string;
  createdUtc: string;
}

type AddUsersToBudgetGroupRequest = components['schemas']['AddUsersToBudgetGroupEndpoint_AddUsersRequest'];
type BudgetSnapshot = components['schemas']['AddUsersToBudgetGroupEndpoint_BudgetSnapshotRequest'];

type ChangeUsersBudgetRoleRequestBody = components['schemas']['ChangeUserBudgetRoleEndpoint_ChangeUsersBudgetRoleRequestBody'];

interface AddUsersParams {
  budgetGroupId: number;
  userIds: string[];
  budget: BudgetSnapshot;
}

interface RemoveUserParams {
  budgetGroupId: number;
  userId: string;
  role: string;
}
interface RemoveUsersBatchParams {
  budgetGroupId: number;
  users: {
    userId: string;
    role: string;
  }[];
}

interface UpdateUserParams {
  budgetGroupId: number;
  userId: string;
  role: string;
}

interface UseManageBudgetGroupUsersReturn {
  addUsers: (budgetGroupId: number, userIds: string[], budget: BudgetSnapshot) => Promise<UserBudgetGroup[] | null>;
  removeUser: (budgetGroupId: number, userId: string, role: string) => Promise<boolean>;
  removeUsers: (budgetGroupId: number, users: { userId: string; role: string }[]) => Promise<boolean>;
  updateUser: (budgetGroupId: number, userId: string, role: string) => Promise<UserBudgetGroup | null>;
  loading: boolean;
  error: string | null;
  removeMutation: UseMutationResult<boolean, unknown, RemoveUserParams, unknown>;
  removeUsersBatchMutation: UseMutationResult<boolean, unknown, RemoveUsersBatchParams, unknown>;
}

interface UseManageBudgetGroupUsersConfig {
  snackbar: SnackbarContextType;
}

export function useManageBudgetGroupUsers({ snackbar }: UseManageBudgetGroupUsersConfig): UseManageBudgetGroupUsersReturn {
  const { organizationId } = useOrganizationId();
  const { getAccessToken } = useAuth();
  const queryClient = useQueryClient();

  const addUsersMutation = useMutation({
    mutationFn: async (params: AddUsersParams) => {
      const requestBody: AddUsersToBudgetGroupRequest = {
        userIds: params.userIds,
        budget: params.budget,
      };

      const client = createApiClient(getAccessToken);

      const result = await client.POST('/api/organizations/{organizationId}/budget-groups/{budgetGroupId}/users', {
        params: {
          path: { organizationId, budgetGroupId: params.budgetGroupId },
        },
        body: requestBody,
      });

      return result.data && Array.isArray(result.data.successes) ? (result.data.successes as UserBudgetGroup[]) : null;
    },
    onSuccess: () => {
      return Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['budget-groups-autocomplete', organizationId],
        }),
        queryClient.invalidateQueries({
          queryKey: ['user-budget-group-membership', organizationId],
          exact: false,
        }),
      ]);
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : 'Failed to add users';
      handleSnackbarError(err, snackbar, message);
    },
  });

  const removeUserMutation = useMutation({
    mutationFn: async (params: RemoveUserParams) => {
      const client = createApiClient(getAccessToken);

      const result = await client.DELETE('/api/organizations/{organizationId}/budget-groups/{budgetGroupId}/users', {
        params: {
          path: { organizationId, budgetGroupId: params.budgetGroupId },
          query: { UserId: params.userId, Role: params.role },
        },
      });

      // DELETE endpoint returns 204 NoContent on success
      const success = result.response.status === 204 || result.response.ok;

      return success;
    },
    /* This will await for an invalidation, our global callback will run first, but this local will run
    immediately after that  */
    onSuccess: () => {
      return Promise.all([
        queryClient.invalidateQueries(
          {
            queryKey: ['usersByBudgetGroup', organizationId],
          },
          { cancelRefetch: false },
        ),
        queryClient.invalidateQueries(
          {
            queryKey: ['budgetGroups', organizationId],
          },
          { cancelRefetch: false },
        ),
      ]);
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : 'Failed to remove user';
      handleSnackbarError(err, snackbar, message);
    },
  });

  const removeUsersBatchMutation = useMutation({
    mutationFn: async (params: RemoveUsersBatchParams) => {
      const client = createApiClient(getAccessToken);

      const result = await client.DELETE('/api/organizations/{organizationId}/budget-groups/{budgetGroupId}/users/batch', {
        params: {
          path: { organizationId, budgetGroupId: params.budgetGroupId },
        },
        body: {
          users: params.users,
        },
      });

      return result.response.ok;
    },
    onSuccess: () => {
      return Promise.all([
        queryClient.invalidateQueries(
          {
            queryKey: ['usersByBudgetGroup', organizationId],
          },
          { cancelRefetch: false },
        ),
        queryClient.invalidateQueries(
          {
            queryKey: ['budgetGroups', organizationId],
          },
          { cancelRefetch: false },
        ),
      ]);
    },
    onError: (err: unknown) => {
      handleSnackbarError(err, snackbar, 'Failed to remove users');
    },
  });

  const updateUserMutation = useMutation({
    mutationFn: async (params: UpdateUserParams) => {
      const requestBody: ChangeUsersBudgetRoleRequestBody = {
        role: params.role,
      };

      const client = createApiClient(getAccessToken);

      const result = await client.PUT('/api/organizations/{organizationId}/budget-groups/{budgetGroupId}/users', {
        params: {
          path: { organizationId, budgetGroupId: params.budgetGroupId },
          query: { UserId: params.userId },
        },
        body: requestBody,
      });

      if (result.error) {
        throw new Error(result.error.toString());
      }

      return result.data as UserBudgetGroup;
    },
    onSuccess: () => {
      return queryClient.invalidateQueries({
        queryKey: ['budgetGroups', organizationId],
        exact: false,
      });
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : 'Failed to update user role';
      handleSnackbarError(err, snackbar, message);
    },
  });

  const addUsers = async (budgetGroupId: number, userIds: string[], budget: BudgetSnapshot): Promise<UserBudgetGroup[] | null> => {
    try {
      return await addUsersMutation.mutateAsync({ budgetGroupId, userIds, budget });
    } catch {
      return null;
    }
  };

  const removeUser = async (budgetGroupId: number, userId: string, role: string): Promise<boolean> => {
    try {
      return await removeUserMutation.mutateAsync({ budgetGroupId, userId, role });
    } catch {
      return false;
    }
  };

  const removeUsers = async (budgetGroupId: number, users: { userId: string; role: string }[]): Promise<boolean> => {
    try {
      return await removeUsersBatchMutation.mutateAsync({ budgetGroupId, users });
    } catch {
      return false;
    }
  };

  const updateUser = async (budgetGroupId: number, userId: string, role: string): Promise<UserBudgetGroup | null> => {
    try {
      return await updateUserMutation.mutateAsync({ budgetGroupId, userId, role });
    } catch {
      return null;
    }
  };

  // Determine the current operation type and overall loading state
  const loading =
    addUsersMutation.isPending || removeUserMutation.isPending || removeUsersBatchMutation.isPending || updateUserMutation.isPending;

  const error =
    addUsersMutation.error instanceof Error
      ? addUsersMutation.error.message
      : removeUserMutation.error instanceof Error
        ? removeUserMutation.error.message
        : removeUsersBatchMutation.error instanceof Error
          ? removeUsersBatchMutation.error.message
          : updateUserMutation.error instanceof Error
            ? updateUserMutation.error.message
            : null;

  return {
    addUsers,
    removeUser,
    removeUsers,
    updateUser,
    loading,
    error,
    removeMutation: removeUserMutation,
    removeUsersBatchMutation: removeUsersBatchMutation,
  };
}
